"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import { useLightbox } from "./lightbox/LightboxContext";

const PLATE_IMAGES = [
  "/assets/siteplan.jpg",
  "/assets/masterplan-gf.jpg",
  "/assets/masterplan-2f.jpg",
  "/assets/plate-a.jpg",
  "/assets/plate-b1f.jpg",
  "/assets/plate-b.jpg",
  "/assets/plate-c.jpg",
];

export function Plates() {
  const t = useTranslations();
  const { open } = useLightbox();
  const swiperRef = useRef<SwiperClass | null>(null);
  const [index, setIndex] = useState(0);

  const plates = t.raw("plates") as { name: string; note: string }[];
  const slides = PLATE_IMAGES.map((src, i) => ({ src, ...plates[i] }));

  return (
    <section className="border-y border-ink/[.08] bg-surface">
      <div className="mx-auto max-w-[1280px] px-[clamp(20px,4vw,40px)] py-[clamp(72px,9vw,120px)]">
        <div
          data-reveal
          className="mb-[clamp(32px,4vw,48px)] flex flex-wrap items-end justify-between gap-[clamp(32px,4vw,48px)]"
        >
          <div className="max-w-[56ch]">
            <p className="mb-[18px] text-xs uppercase tracking-[.2em] text-accent">
              {t("platesKicker")}
            </p>
            <h3 className="text-[clamp(24px,2.6vw,34px)] leading-[1.15]">
              {t("platesTitle")}
            </h3>
            <p className="mt-3.5 text-[15px] leading-[1.7] font-light text-muted">
              {t("platesSub")}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label={t("prev")}
              className="flex h-[38px] w-[38px] items-center justify-center rounded-[3px] border border-ink/[.16] bg-none text-[16px] transition-colors hover:bg-ink hover:text-white"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => swiperRef.current?.slideNext()}
              aria-label={t("next")}
              className="flex h-[38px] w-[38px] items-center justify-center rounded-[3px] border border-ink/[.16] bg-none text-[16px] transition-colors hover:bg-ink hover:text-white"
            >
              →
            </button>
          </div>
        </div>
        <div
          data-reveal
          className="relative h-[clamp(380px,48vw,620px)] w-full overflow-hidden border border-ink/10 bg-bg"
        >
          <Swiper
            onSwiper={(s) => (swiperRef.current = s)}
            onSlideChange={(s) => setIndex(s.activeIndex)}
            className="h-full w-full"
          >
            {slides.map((slide) => (
              <SwiperSlide key={slide.src}>
                <div className="relative h-full w-full p-[18px]">
                  <Image
                    src={slide.src}
                    alt={slide.name}
                    fill
                    sizes="100vw"
                    className="object-contain"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <button
            type="button"
            onClick={() =>
              open(
                slides.map((s) => ({ src: s.src, caption: s.name })),
                index,
              )
            }
            aria-label="Fullscreen"
            className="absolute right-4 top-4 z-10 flex h-[38px] w-[38px] items-center justify-center rounded-[3px] border-0 bg-ink/70 text-[22px] leading-none text-white transition-colors hover:bg-ink"
          >
            +
          </button>
        </div>
        <p data-reveal className="mt-3.5 text-[13px] text-muted">
          {slides[index]?.name} · {index + 1} / {slides.length}
        </p>
      </div>
    </section>
  );
}
